import {
  AccessBindingResource,
  AccessResourceSignal,
  AccessRoleResource,
} from 'adp-access-common';

export type AccessManagedResource = AccessRoleResource | AccessBindingResource;
export type ProcessedAccessSignal = {
  revision: string;
  changedAt: string;
};

export function getManagedResourceKey(resource: AccessManagedResource): string {
  return resource.metadata.uid ?? resource.metadata.name;
}

export function getManagedResourceSignalKey(
  signal: AccessResourceSignal,
): string {
  return `${signal.data.resourceKind}:${signal.data.resourceRef}`;
}

export function matchesManagedResourceSignal(
  resource: AccessManagedResource,
  signal: AccessResourceSignal,
): boolean {
  return (
    resource.kind === signal.data.resourceKind &&
    (resource.metadata.uid === signal.data.resourceRef ||
      resource.metadata.name === signal.data.resourceName)
  );
}

export function upsertManagedResource<T extends AccessManagedResource>(
  items: T[],
  next: T,
): T[] {
  const nextKey = getManagedResourceKey(next);
  const index = items.findIndex(item => getManagedResourceKey(item) === nextKey);

  if (index === -1) {
    return [...items, next].sort((left, right) =>
      left.metadata.name.localeCompare(right.metadata.name),
    );
  }

  const copy = [...items];
  copy[index] = next;
  return copy;
}

export function patchManagedResourceBySignal<T extends AccessManagedResource>(
  items: T[],
  signal: AccessResourceSignal,
): T[] {
  return items.map(item => {
    if (!matchesManagedResourceSignal(item, signal)) {
      return item;
    }

    const nextPhase = signal.data.phase ?? item.status?.phase;
    const nextStatus = nextPhase
      ? {
          ...item.status,
          phase: nextPhase,
          message: signal.data.message ?? item.status?.message,
          lastRunId: signal.data.runId ?? item.status?.lastRunId,
          lastReconciledAt:
            signal.data.changedAt ?? item.status?.lastReconciledAt,
        }
      : item.status;

    return {
      ...item,
      metadata: {
        ...item.metadata,
        updatedAt: signal.data.changedAt,
      },
      status: nextStatus,
    };
  });
}

export function removeManagedResourceBySignal<T extends AccessManagedResource>(
  items: T[],
  signal: AccessResourceSignal,
): T[] {
  return items.filter(item => !matchesManagedResourceSignal(item, signal));
}

export function isSignalNewerThanPrevious(
  previousSignal: ProcessedAccessSignal | undefined,
  signal: AccessResourceSignal,
): boolean {
  if (!previousSignal) {
    return true;
  }

  if (previousSignal.changedAt < signal.data.changedAt) {
    return true;
  }

  if (previousSignal.changedAt > signal.data.changedAt) {
    return false;
  }

  return previousSignal.revision !== signal.revision;
}
